import React, { useCallback, useState } from 'react';
import styled from 'styled-components';
import { COLORS, FlexView, Row } from '@cuemath/comps';
import { Button, Text, TextInput } from '@cuemath/web-comps';

import { postStudentTransferStatus } from 'actions/student-transfer/single-student';

import {
  InfoWrapper,
  LinkText,
  NameCard,
  NameCardWrapper,
} from '../slots-pending/styles';
import StudentTransferSlotsList from '../slots-pending/add-slots';
import { POCValue } from '../../../../screens/student-details/styled-comps';
import {
  getSingleStudentTransferPostData,
  singleStudentTransferButtonDisable,
} from '../../../../utils/student-transfer';

const Root = styled(FlexView)`
  gap: 16px;
  padding: 8px 0px;
`;

const InputWrapper = styled(Row)`
  width: 100%;
  height: 44px;

  div {
    width: 100%;
  }
`;

const slotsInitState = [
  {
    day: '',
    time: '',
    id: `student-transfer-slots-${1}`,
  },
];

const EditStudentTransferSlots = ({ data }) => {
  const {
    slotTime,
    reasonForTransfer,
    studentId,
    courseStream,
    preferredTeacherId,
    assigningSingleStudent,
  } = data || {};
  const [slotsList, setSlotsList] = useState(slotsInitState);
  const [errorMessage, setErrorMessage] = useState('');
  const [didAssign, setDidAssign] = useState(false);
  const [teacherToBeAssigned, setTeacherToBeAssigned] = useState('');

  const updateSlotsList = useCallback(updateSlotData => {
    setSlotsList(prev =>
      prev.map(slot => {
        if (slot.id === updateSlotData.id) {
          return updateSlotData;
        }
        return slot;
      }),
    );
  }, []);

  const addMoreSlots = () => {
    setSlotsList(prev => [
      ...prev,
      {
        day: '',
        time: '',
        id: `student-transfer-slots-${prev.length + 1}`,
      },
    ]);
  };

  const onStudentTransferCompletion = error => {
    if (error) {
      setErrorMessage(error);
    } else {
      setDidAssign(true);
      setErrorMessage('');

      if (typeof assigningSingleStudent === 'function') {
        assigningSingleStudent();
      }
    }
  };

  const { post: initiateStudentTransfer, isProcessing } = postStudentTransferStatus({
    onComplete: onStudentTransferCompletion,
  });

  const handleSlotUpdate = () => {
    const finalData = getSingleStudentTransferPostData({
      transferReason: reasonForTransfer,
      slotsList,
    });

    if (reasonForTransfer === 'PREFERRED_TEACHER') {
      finalData.course_stream = courseStream;
      finalData.preferred_teacher_id = preferredTeacherId;
    }

    initiateStudentTransfer(finalData, studentId);
  };
  const message =
    typeof errorMessage !== 'string'
      ? errorMessage?.course_stream || errorMessage?.preferred_teacher_id
      : errorMessage;

  const disableBtn = singleStudentTransferButtonDisable({
    transferReason: 'Class slot issues',
    slotsList,
  });

  const collectPreferredTeacherId = reasonForTransfer === 'PREFERRED_TEACHER';

  return (
    <Root>
      <NameCardWrapper>
        <NameCard variant="eyebrow2">Previous slot info</NameCard>
        <POCValue variant="cta1">{slotTime}</POCValue>
      </NameCardWrapper>

      {collectPreferredTeacherId && (
        <>
          <NameCard variant="eyebrow2">Preferred teacher id</NameCard>
          <InputWrapper>
            <TextInput
              onChange={setTeacherToBeAssigned}
              value={teacherToBeAssigned}
              width="100%"
              label="Enter teacher ID"
            />
          </InputWrapper>
        </>
      )}

      <InfoWrapper gap={8}>
        <Row alignItem="center" justifyContent="space-between">
          <NameCard variant="eyebrow2">Regular Slots (IST)</NameCard>
          <LinkText variant="body1Regular" onClick={() => setSlotsList(slotsInitState)}>
            Reset
          </LinkText>
        </Row>

        {slotsList.map(slotData => {
          return (
            <StudentTransferSlotsList
              key={slotData.id}
              {...slotData}
              updateSlotsList={updateSlotsList}
              slotsList={slotsList}
            />
          );
        })}

        <LinkText variant="body1Regular" onClick={addMoreSlots}>
          Add more
        </LinkText>
      </InfoWrapper>

      <Button
        width="100%"
        isBusy={isProcessing}
        onClick={handleSlotUpdate}
        label="Update slots"
        isDisabled={disableBtn || didAssign}
      />
      {message && (
        <Text color={COLORS.RED_SHADE} variant="eyebrow2">
          {message}
        </Text>
      )}
    </Root>
  );
};

export default EditStudentTransferSlots;
